import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Ticket, LayoutGrid, Flame, User, Loader2, MessageCircle, Megaphone,
  ExternalLink, CheckCircle2, Clock, Hourglass, Archive, RefreshCw, Info,
} from 'lucide-react';
import { DndContext, useDraggable, useDroppable, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { Header } from '../components/Header';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';
import { EmptyState } from '../components/EmptyState';
import { InfoTip } from '../components/InfoTip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLUMNS = [
  { id: 'open', label: 'Abiertos', icon: Ticket, accent: 'text-brand-deep dark:text-brand-green', ring: 'border-brand-green/30' },
  { id: 'in_progress', label: 'En progreso', icon: Clock, accent: 'text-brand-deep dark:text-brand-deep dark:text-brand-green', ring: 'border-violet-500/30' },
  { id: 'waiting_customer', label: 'Esperando cliente', icon: Hourglass, accent: 'text-amber-700 dark:text-amber-400', ring: 'border-amber-500/30' },
  { id: 'resolved', label: 'Resueltos', icon: CheckCircle2, accent: 'text-emerald-700 dark:text-emerald-400', ring: 'border-emerald-500/30' },
  { id: 'closed', label: 'Cerrados', icon: Archive, accent: 'text-muted-foreground', ring: 'border-slate-500/30' },
];

const PRIORITY_COLUMNS = [
  { id: 'urgent', label: 'Urgente', icon: Flame, accent: 'text-red-600 dark:text-red-400', ring: 'border-red-500/30' },
  { id: 'high', label: 'Alta', icon: Flame, accent: 'text-amber-700 dark:text-amber-400', ring: 'border-orange-500/30' },
  { id: 'normal', label: 'Normal', icon: Ticket, accent: 'text-brand-deep dark:text-brand-green', ring: 'border-brand-green/30' },
  { id: 'low', label: 'Baja', icon: Ticket, accent: 'text-muted-foreground', ring: 'border-slate-500/30' },
];

const PRIORITY_STYLE = {
  urgent: 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30',
  high: 'bg-orange-500/15 text-amber-700 dark:text-amber-400 border-orange-500/30',
  normal: 'bg-brand-green/15 text-brand-deep dark:text-brand-green border-brand-green/30',
  low: 'bg-slate-500/15 text-muted-foreground border-slate-500/30',
};

const STATUS_LABEL = {
  open: 'Abierto', in_progress: 'En progreso', waiting_customer: 'Esperando cliente',
  resolved: 'Resuelto', closed: 'Cerrado',
};

const CATEGORY_LABEL = {
  sale: 'Venta', support: 'Soporte', complaint: 'Reclamo',
  technical: 'Técnico', billing: 'Facturación', info: 'Información',
};

const TicketCard = ({ ticket, onOpen, groupBy }) => {
  const navigate = useNavigate();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: ticket.id });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      data-testid={`ticket-card-${ticket.id}`}
      onClick={() => onOpen(ticket)}
      className={cn(
        'group p-3 rounded-xl bg-card border border-border/60 cursor-grab active:cursor-grabbing',
        'transition-[box-shadow,border-color,transform] hover:border-violet-500/40 hover:shadow-lg hover:shadow-violet-500/5',
        isDragging && 'opacity-60 shadow-2xl z-50'
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium leading-snug line-clamp-2">{ticket.subject}</p>
        {groupBy === 'status' && (
          <Badge variant="outline" className={cn('text-[10px] shrink-0', PRIORITY_STYLE[ticket.priority])}>
            {ticket.priority}
          </Badge>
        )}
        {groupBy === 'priority' && (
          <Badge variant="outline" className="text-[10px] shrink-0">{STATUS_LABEL[ticket.status]}</Badge>
        )}
      </div>

      {ticket.description && (
        <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{ticket.description}</p>
      )}

      <div className="mt-2.5 flex flex-wrap items-center gap-1.5 text-[11px]">
        <Badge variant="secondary" className="text-[10px]">{CATEGORY_LABEL[ticket.category] || ticket.category}</Badge>
        {ticket.customer_name && (
          <button
            data-testid={`ticket-customer-link-${ticket.id}`}
            onClick={(e) => { e.stopPropagation(); navigate('/customers'); }}
            className="inline-flex items-center gap-1 text-brand-deep dark:text-brand-deep dark:text-brand-green hover:underline"
          >
            <User className="w-3 h-3" /> {ticket.customer_name}
          </button>
        )}
        {ticket.campaign_name && (
          <button
            data-testid={`ticket-campaign-link-${ticket.id}`}
            onClick={(e) => { e.stopPropagation(); navigate('/campaigns'); }}
            className="inline-flex items-center gap-1 text-fuchsia-700 dark:text-fuchsia-300 hover:underline"
          >
            <Megaphone className="w-3 h-3" /> {ticket.campaign_name}
          </button>
        )}
        {ticket.chat_id && (
          <button
            data-testid={`ticket-chat-link-${ticket.id}`}
            onClick={(e) => { e.stopPropagation(); navigate('/inbox'); }}
            className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400 hover:underline"
          >
            <MessageCircle className="w-3 h-3" /> Ver chat
          </button>
        )}
      </div>

      <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
        <span>{ticket.assignee_name ? `👤 ${ticket.assignee_name}` : 'Sin asignar'}</span>
        <span>{ticket.created_at ? new Date(ticket.created_at).toLocaleDateString('es') : ''}</span>
      </div>
    </div>
  );
};

const Column = ({ column, tickets, onOpen, groupBy }) => {
  const { setNodeRef, isOver } = useDroppable({ id: column.id });
  const Icon = column.icon;
  return (
    <div
      ref={setNodeRef}
      data-testid={`ticket-column-${column.id}`}
      className={cn(
        'flex-shrink-0 w-[280px] rounded-2xl border bg-muted/20 p-3 transition-colors',
        isOver ? cn('bg-muted/50', column.ring) : 'border-border/40'
      )}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className={cn('w-4 h-4', column.accent)} />
          <span className="text-sm font-semibold">{column.label}</span>
        </div>
        <Badge variant="secondary" className="text-[10px]" data-testid={`ticket-count-${column.id}`}>{tickets.length}</Badge>
      </div>
      <div className="space-y-2 min-h-[80px]">
        {tickets.map((t) => <TicketCard key={t.id} ticket={t} onOpen={onOpen} groupBy={groupBy} />)}
        {tickets.length === 0 && (
          <p className="text-xs text-muted-foreground/70 text-center py-6">Arrastra tickets aquí</p>
        )}
      </div>
    </div>
  );
};

export default function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [agents, setAgents] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupBy, setGroupBy] = useState('status');
  const [filters, setFilters] = useState({ agent: 'all', campaign_id: 'all', priority: 'all' });
  const [detail, setDetail] = useState(null);
  const [resolution, setResolution] = useState('');
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.campaign_id !== 'all') params.append('campaign_id', filters.campaign_id);
      if (filters.priority !== 'all') params.append('priority', filters.priority);
      const res = await axios.get(`${API}/tickets?${params.toString()}`);
      setTickets(res.data.tickets || []);
    } catch {
      toast.error('No se pudieron cargar los tickets');
    } finally {
      setLoading(false);
    }
  }, [filters.campaign_id, filters.priority]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/users`).then((r) => setAgents(r.data || [])).catch(() => setAgents([]));
    axios.get(`${API}/campaigns`).then((r) => setCampaigns(r.data || [])).catch(() => setCampaigns([]));
  }, []);

  const columns = groupBy === 'status' ? STATUS_COLUMNS : PRIORITY_COLUMNS;

  const visible = useMemo(() => tickets.filter((t) => {
    if (filters.agent === 'unassigned') return !t.assignee_user_id;
    if (filters.agent !== 'all') return t.assignee_user_id === filters.agent;
    return true;
  }), [tickets, filters.agent]);

  const grouped = useMemo(() => {
    const map = {};
    columns.forEach((c) => { map[c.id] = []; });
    visible.forEach((t) => {
      const key = groupBy === 'status' ? (t.status || 'open') : (t.priority || 'normal');
      if (map[key]) map[key].push(t);
    });
    return map;
  }, [visible, columns, groupBy]);

  const handleDragEnd = async ({ active, over }) => {
    if (!over) return;
    const ticket = tickets.find((t) => t.id === active.id);
    const field = groupBy === 'status' ? 'status' : 'priority';
    if (!ticket || ticket[field] === over.id) return;
    setTickets((prev) => prev.map((t) => (t.id === ticket.id ? { ...t, [field]: over.id } : t)));
    try {
      await axios.put(`${API}/tickets/${ticket.id}`, { [field]: over.id });
      toast.success(`Ticket movido a ${over.id}`);
    } catch {
      toast.error('No se pudo mover el ticket');
      load();
    }
  };

  const patchDetail = async (payload) => {
    try {
      await axios.put(`${API}/tickets/${detail.id}`, payload);
      toast.success('Ticket actualizado');
      setDetail({ ...detail, ...payload });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al actualizar');
    }
  };

  const stats = [
    { label: 'Abiertos', value: tickets.filter((t) => t.status === 'open').length, color: 'text-brand-deep dark:text-brand-green', to: 'open',
      info: { title: 'Tickets abiertos', text: 'Casos que todavía nadie ha tomado. Noah los crea cuando detecta que hace falta un humano.', source: 'Tickets con estado Abierto', to: '/automations', linkLabel: 'Ver reglas de escalación' } },
    { label: 'Urgentes', value: tickets.filter((t) => ['urgent', 'high'].includes(t.priority)).length, color: 'text-red-600 dark:text-red-400', to: 'urgent',
      info: { title: 'Tickets urgentes', text: 'Prioridad alta o urgente. Suenan un aviso y te llega notificación del navegador si no estás silenciado.', source: 'Prioridad asignada por Noah o por ti', to: '/inbox', linkLabel: 'Atender en el Inbox' } },
    { label: 'Sin asignar', value: tickets.filter((t) => !t.assignee_user_id).length, color: 'text-amber-700 dark:text-amber-400', to: 'unassigned',
      info: { title: 'Sin asignar', text: 'Nadie del equipo es responsable todavía. Abre el ticket para asignarlo a un agente.', source: 'Tickets sin campo assignee' } },
    { label: 'Resueltos', value: tickets.filter((t) => ['resolved', 'closed'].includes(t.status)).length, color: 'text-emerald-700 dark:text-emerald-400', to: 'resolved',
      info: { title: 'Resueltos', text: 'Casos cerrados con su nota de resolución. Sirven para medir el tiempo de respuesta del equipo.', source: 'Tickets en estado Resuelto o Cerrado', to: '/reports', linkLabel: 'Ver reportes' } },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="tickets-page">
      <Header title="Tickets & Escalaciones" subtitle="Noah escala aquí lo que necesita un humano" />

      <div className="p-4 sm:p-6 space-y-5">
        {/* KPI strip — cada tarjeta filtra el tablero */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {stats.map((s) => (
            <div key={s.label} className="relative">
            <button
              data-testid={`ticket-stat-${s.to}`}
              onClick={() => {
                if (s.to === 'unassigned') { setFilters((f) => ({ ...f, agent: 'unassigned' })); return; }
                if (s.to === 'urgent') { setGroupBy('priority'); return; }
                setGroupBy('status');
              }}
              className="group w-full p-4 rounded-2xl bg-card border border-border/60 text-left transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5"
            >
              <p className={cn('text-2xl font-bold', s.color)}>{s.value}</p>
              <p className="text-xs text-muted-foreground flex items-center gap-1 pr-5">
                {s.label} <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-60 transition-opacity" />
              </p>
            </button>
            {s.info && <div className="absolute top-3 right-3"><InfoTip {...s.info} testId={`ticket-stat-${s.to}-info`} /></div>}
            </div>
          ))}
        </div>

        {/* Controls */}
        <Card className="border-border/60">
          <CardContent className="p-3 flex flex-wrap items-center gap-2">
            <div className="flex rounded-xl bg-muted/40 p-1">
              <button
                data-testid="tickets-view-status"
                onClick={() => setGroupBy('status')}
                className={cn('px-3 py-1.5 text-xs rounded-lg transition-colors flex items-center gap-1.5',
                  groupBy === 'status' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground')}
              >
                <LayoutGrid className="w-3.5 h-3.5" /> Por estado
              </button>
              <button
                data-testid="tickets-view-priority"
                onClick={() => setGroupBy('priority')}
                className={cn('px-3 py-1.5 text-xs rounded-lg transition-colors flex items-center gap-1.5',
                  groupBy === 'priority' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground')}
              >
                <Flame className="w-3.5 h-3.5" /> Por prioridad
              </button>
            </div>

            <Select value={filters.agent} onValueChange={(v) => setFilters({ ...filters, agent: v })}>
              <SelectTrigger className="w-[190px] h-9 text-xs" data-testid="tickets-filter-agent">
                <SelectValue placeholder="Agente" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los agentes</SelectItem>
                <SelectItem value="unassigned">Sin asignar</SelectItem>
                {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name || a.email}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={filters.campaign_id} onValueChange={(v) => setFilters({ ...filters, campaign_id: v })}>
              <SelectTrigger className="w-[190px] h-9 text-xs" data-testid="tickets-filter-campaign">
                <SelectValue placeholder="Campaña" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las campañas</SelectItem>
                {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>

            <Button variant="outline" size="sm" onClick={load} data-testid="tickets-refresh" className="h-9 gap-1">
              <RefreshCw className="w-3.5 h-3.5" /> Actualizar
            </Button>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground cursor-help" data-testid="tickets-help">
                    <Info className="w-3.5 h-3.5" /> ¿Cómo funciona?
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs">
                  Arrastra un ticket entre columnas para cambiar su {groupBy === 'status' ? 'estado' : 'prioridad'}.
                  Toca un ticket para asignarlo o resolverlo. Los enlaces llevan al cliente, la campaña o el chat de origen.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardContent>
        </Card>

        {/* Kanban */}
        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-muted-foreground" /></div>
        ) : tickets.length === 0 ? (
          <Card className="border-dashed border-border/60">
            <CardContent className="p-8">
              <EmptyState
                mood="sleep"
                title="Ningún ticket abierto"
                description="Todo tranquilo. Cuando Noah escale una conversación o crees un ticket manual, aparecerá en este tablero."
                testId="tickets-empty"
              />
            </CardContent>
          </Card>
        ) : (
          <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
            <div className="flex gap-3 overflow-x-auto pb-4" data-testid="tickets-kanban">
              {columns.map((col) => (
                <Column key={col.id} column={col} tickets={grouped[col.id] || []} onOpen={(t) => { setDetail(t); setResolution(t.resolution || ''); }} groupBy={groupBy} />
              ))}
            </div>
          </DndContext>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="ticket-detail-dialog">
          <DialogHeader><DialogTitle className="text-base">{detail?.subject}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{detail.description || 'Sin descripción'}</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Estado</Label>
                  <Select value={detail.status} onValueChange={(v) => patchDetail({ status: v })}>
                    <SelectTrigger className="mt-1 h-9 text-xs" data-testid="ticket-detail-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUS_COLUMNS.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Prioridad</Label>
                  <Select value={detail.priority} onValueChange={(v) => patchDetail({ priority: v })}>
                    <SelectTrigger className="mt-1 h-9 text-xs" data-testid="ticket-detail-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PRIORITY_COLUMNS.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Agente asignado</Label>
                <Select value={detail.assignee_user_id || ''} onValueChange={(v) => patchDetail({ assignee_user_id: v })}>
                  <SelectTrigger className="mt-1 h-9 text-xs" data-testid="ticket-detail-assignee">
                    <SelectValue placeholder="Sin asignar" />
                  </SelectTrigger>
                  <SelectContent>
                    {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name || a.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Resolución</Label>
                <Textarea className="mt-1 text-sm" rows={3} value={resolution}
                  data-testid="ticket-detail-resolution"
                  onChange={(e) => setResolution(e.target.value)} placeholder="Qué se hizo para resolverlo" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setDetail(null)} data-testid="ticket-detail-close">Cerrar</Button>
            <Button size="sm" data-testid="ticket-detail-save"
              className="bg-primary"
              onClick={() => patchDetail({ resolution, status: 'resolved' })}>
              Guardar y resolver
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
